import type { HttpPort } from "~/lib/http";

export interface AdminInfo { id: string; email: string; displayName: string; canApprove?: boolean }
export interface TeacherRow {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  schoolName: string | null;
  status: "active" | "suspended";
  lastLoginAt: string | null;
  createdAt: string;
  classCount: number;
  studentCount: number;
}
export interface AuditEntry {
  id: number;
  action: string;
  admin: string | null;
  targetType: string | null;
  targetId: string | null;
  reason: string | null;
  createdAt: string;
}
export interface Paged<T> { items: T[]; total: number; page: number; limit: number }
export interface SystemInfo {
  version: string | null;
  node: string;
  migrations: string[];
  backup: { result: string | null; message: string | null; finishedAt: string | null; lastSuccessAt: string | null; restoreTest: string | null } | null;
}

export type RegistrationStatus = "email_pending" | "awaiting_approval" | "approved" | "rejected" | "completed" | "expired";
export interface RegistrationRow {
  id: string;
  status: RegistrationStatus;
  schoolName: string;
  schoolType: string | null;
  schoolNumber: string | null;
  street: string;
  postalCode: string;
  city: string;
  estClasses: number | null;
  contactName: string;
  contactFunction: string;
  contactEmail: string;
  hints: { freemail?: boolean; similar?: number };
  createdAt: string;
  confirmedAt: string | null;
  reviewedAt: string | null;
  reviewReason: string | null;
  approvalExpiresAt: string | null;
  schoolId: string | null;
}
export type RegistrationFilter = "open" | "waiting" | "all";

export type TeacherAction = "suspend" | "reactivate" | "revoke-sessions";

export type SchoolStatus = "trial" | "active" | "expired" | "suspended" | "deleted";
export interface SchoolRow {
  id: string;
  name: string;
  schoolType: string | null;
  schoolNumber: string | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  state: string | null;
  estClasses: number | null;
  status: SchoolStatus;
  storedStatus: SchoolStatus;
  trialStartedAt: string | null;
  trialEndsAt: string | null;
  licenseKind: "none" | "manual";
  licenseEndsAt: string | null;
  createdAt: string;
  memberCount?: number;
  classCount?: number;
}
export interface SchoolMember { userId: string; email: string; firstName: string; lastName: string; role: "owner" | "teacher"; accountStatus: string; classCount: number }
export interface SchoolLogEntry { from: string | null; to: string; reason: string; admin: string | null; createdAt: string }
export interface SchoolDetail { school: SchoolRow; members: SchoolMember[]; log: SchoolLogEntry[] }
export interface NewSchool {
  name: string;
  schoolType: string;
  schoolNumber: string;
  street: string;
  postalCode: string;
  city: string;
  estClasses: string;
  mode: "trial" | "manual";
  licenseEndsOn: string;
  reason: string;
}
export type SchoolAction = "members" | "members/remove" | "extend-trial" | "manual-license" | "suspend" | "reactivate";

export function createAdminApi(http: HttpPort) {
  let csrf = "";
  const post = <T>(path: string, body: unknown = {}) => http.request<T>({ path: `/admin${path}`, method: "POST", body, token: csrf });
  const get = <T>(path: string) => http.request<T>({ path: `/admin${path}` });

  return {
    login(email: string, password: string) {
      return post<{ challengeToken: string }>("/login", { email, password });
    },
    async verify(challengeToken: string, code: string) {
      const result = await post<{ admin: AdminInfo; csrfToken: string; recoveryCodesLeft?: number }>("/login/verify", { challengeToken, code });
      csrf = result.csrfToken;
      return result;
    },
    async me() {
      const result = await get<{ admin: AdminInfo; csrfToken: string }>("/me");
      csrf = result.csrfToken;
      return result.admin;
    },
    async logout() {
      await post("/logout");
      csrf = "";
    },
    async teachers(page: number, search: string): Promise<Paged<TeacherRow>> {
      const query = new URLSearchParams({ page: String(page), ...(search ? { q: search } : {}) });
      const result = await get<{ teachers: TeacherRow[]; total: number; page: number; limit: number }>(`/teachers?${query}`);
      return { items: result.teachers, total: result.total, page: result.page, limit: result.limit };
    },
    teacherAction(id: string, action: TeacherAction, reason: string) {
      return post(`/teachers/${encodeURIComponent(id)}/${action}`, { reason });
    },
    async auditLog(page: number): Promise<Paged<AuditEntry>> {
      const result = await get<{ entries: AuditEntry[]; total: number; page: number; limit: number }>(`/audit-log?page=${page}`);
      return { items: result.entries, total: result.total, page: result.page, limit: result.limit };
    },
    system() {
      return get<SystemInfo>("/system");
    },
    async schools(page: number, search: string): Promise<Paged<SchoolRow>> {
      const query = new URLSearchParams({ page: String(page), ...(search ? { q: search } : {}) });
      const result = await get<{ schools: SchoolRow[]; total: number; page: number; limit: number }>(`/schools?${query}`);
      return { items: result.schools, total: result.total, page: result.page, limit: result.limit };
    },
    school(id: string) {
      return get<SchoolDetail>(`/schools/${encodeURIComponent(id)}`);
    },
    createSchool(input: NewSchool) {
      const { estClasses, licenseEndsOn, ...rest } = input;
      return post<{ id: string }>("/schools", { ...rest, ...(estClasses ? { estClasses: Number(estClasses) } : {}), ...(licenseEndsOn ? { licenseEndsOn } : {}) });
    },
    async registrations(filter: RegistrationFilter, page = 1) {
      const result = await get<{ registrations: RegistrationRow[]; total: number; waiting: number; page: number; limit: number }>(`/registrations?filter=${filter}&page=${page}`);
      return { items: result.registrations, total: result.total, waiting: result.waiting, page: result.page, limit: result.limit };
    },
    registrationDecision(id: string, decision: "approve" | "reject", reason: string) {
      return post(`/registrations/${encodeURIComponent(id)}/${decision}`, { reason });
    },
    schoolAction(id: string, action: SchoolAction, body: Record<string, unknown>) {
      return post(`/schools/${encodeURIComponent(id)}/${action}`, body);
    },
    fixClassSchoolIds() {
      return post<{ fixed: number }>("/maintenance/fix-class-school-ids");
    }
  };
}

export type AdminApi = ReturnType<typeof createAdminApi>;

const ACTION_LABELS: Record<string, string> = {
  "admin.login": "Anmeldung",
  "admin.login_password_ok": "Passwort geprüft",
  "admin.login_failed": "Anmeldung fehlgeschlagen",
  "admin.logout": "Abmeldung",
  "teacher.suspend": "Lehrkraft gesperrt",
  "teacher.reactivate": "Lehrkraft entsperrt",
  "teacher.revoke_sessions": "Sitzungen beendet",
  "registration.approve": "Anfrage freigegeben",
  "registration.reject": "Anfrage abgelehnt",
  "school.create": "Schule angelegt",
  "school.add_member": "Mitglied zur Schule hinzugefügt",
  "school.remove_member": "Mitglied aus Schule entfernt",
  "school.extend_trial": "Test verlängert",
  "school.manual_license": "Manuelle Lizenz gesetzt",
  "school.suspend": "Schule gesperrt",
  "school.reactivate": "Schule entsperrt",
  "maintenance.fix_class_school_ids": "Wartung: Klassen-Schulzuordnung repariert",
  "cli.create_admin": "Admin angelegt (Terminal)",
  "cli.reset_2fa": "Zwei-Faktor neu eingerichtet (Terminal)",
  "cli.reset_password": "Passwort geändert (Terminal)",
  "cli.revoke_sessions": "Admin-Sitzungen beendet (Terminal)"
};

export function actionLabel(action: string) {
  return ACTION_LABELS[action] ?? action;
}

export function formatDate(value: string | null) {
  if (!value) return "nie";
  return new Date(value).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
}

export const STATUS_LABELS: Record<string, string> = {
  trial: "Test",
  active: "Lizenz",
  expired: "Abgelaufen",
  suspended: "Gesperrt",
  deleted: "Gelöscht"
};

export function formatDay(value: string | null) {
  if (!value) return "–";
  return new Date(value).toLocaleDateString("de-DE", { dateStyle: "medium" });
}

export const REGISTRATION_LABELS: Record<RegistrationStatus, string> = {
  email_pending: "E-Mail unbestätigt",
  awaiting_approval: "Wartet auf Freigabe",
  approved: "Freigegeben, Vertrag offen",
  rejected: "Abgelehnt",
  completed: "Test läuft",
  expired: "Abgelaufen"
};
